import React, { useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import { Check } from "lucide-react";
import { ErrorText } from "features/bots/common/styles";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography.ts";
import { BoldPlusOutlined, BoldRemoveOutlined } from "common/Icons/Icons.tsx";
import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Tooltip from "antd/es/tooltip";
import { Divider } from "common/styles.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import useMedia from "utils/hooks/useMedia.ts";
import {
  MOBILE_SEARCH,
  PURCHASE_API_SLOTS_SETTINGS_PATH,
} from "common/constants.ts";
import { SMALL_MOBILE_CHECKOUT_HEIGHT } from "features/settings/common/constants.ts";
import {
  MIN_API_SLOTS,
  API_SLOTS_STEP,
  getApiSlotsValidation,
} from "features/settings/common/apiSlotsValidation.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import TypographyLink from "common/Typography/Link";
import { useHistory } from "react-router-dom";

interface ApiSlotsSelectorProps {
  isBottomSummary?: boolean;
  isLocked?: boolean;
}

const ApiSlotsSelector = ({
  isBottomSummary,
  isLocked,
}: ApiSlotsSelectorProps) => {
  const { numApiSlots, setNumApiSlots, isStandaloneApiSlots, isExtend } =
    useContext(BuySubscriptionContext)!;
  const showTitle = !isBottomSummary;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();
  const history = useHistory();

  const { hasMinSlotsError, isInvalidStep: isInvalidStepRaw } =
    getApiSlotsValidation(numApiSlots ?? 0);
  const isInvalidStep = !isExtend && isInvalidStepRaw;

  const onRemoveClick = () => {
    setNumApiSlots((prev) => {
      if (isStandaloneApiSlots) {
        if (!prev || prev <= MIN_API_SLOTS) return MIN_API_SLOTS;
      } else {
        if (!prev || prev <= MIN_API_SLOTS) return 0;
      }
      const rounded = Math.ceil(prev / API_SLOTS_STEP) * API_SLOTS_STEP;
      return rounded > prev ? rounded - API_SLOTS_STEP : prev - API_SLOTS_STEP;
    });
  };

  const onAddClick = () => {
    setNumApiSlots((prev) => {
      if (!prev) return MIN_API_SLOTS;
      const rounded = Math.floor(prev / API_SLOTS_STEP) * API_SLOTS_STEP;
      return rounded < prev ? rounded + API_SLOTS_STEP : prev + API_SLOTS_STEP;
    });
  };

  const onNumSlotsChange = (value: number) => {
    setNumApiSlots(value);
  };

  const numOfApiSlots = (
    <>
      <Flex>
        <div>
          <Flex $justifyContent="flex-start">
            <ApiSlotsTitleText>
              {t("api_slots.num_api_slots.title")}
            </ApiSlotsTitleText>
          </Flex>
        </div>
        <StyledInputNumber
          value={numApiSlots}
          onChange={isLocked ? undefined : onNumSlotsChange}
          controls={false}
          disabled={isLocked}
          addonAfter={
            <Flex>
              <IconWrapper onClick={isLocked ? undefined : onRemoveClick}>
                <RemoveIcon />
              </IconWrapper>
              <BorderVertical />
              <IconWrapper onClick={isLocked ? undefined : onAddClick}>
                <PlusIcon />
              </IconWrapper>
            </Flex>
          }
          style={{ minWidth: 140, width: 140, marginLeft: 12 }}
        />
      </Flex>
      {isStandaloneApiSlots ? (
        <>
          {hasMinSlotsError ? (
            <ErrorText>
              {numApiSlots === 0
                ? t("api_slots.choose_api_slots_error", { min: MIN_API_SLOTS })
                : t("api_slots.min_slots_error", { min: MIN_API_SLOTS })}
            </ErrorText>
          ) : isInvalidStep ? (
            <ErrorText>
              {t("api_slots.invalid_step_error", { step: API_SLOTS_STEP })}
            </ErrorText>
          ) : null}
        </>
      ) : (
        <>
          {hasMinSlotsError && (numApiSlots ?? 0) > 0 && (
            <ErrorText>
              {t("api_slots.min_slots_error", { min: MIN_API_SLOTS })}
            </ErrorText>
          )}
          {isInvalidStep && (
            <ErrorText>
              {t("api_slots.invalid_step_error", { step: API_SLOTS_STEP })}
            </ErrorText>
          )}
        </>
      )}
    </>
  );

  if (isStandaloneApiSlots) {
    return (
      <div>
        {showTitle && (
          <>
            {/* The new look shows the title above the checkout cards. */}
            {!isNewLook && (
              <Title
                style={{
                  fontSize: isMobile ? undefined : typography.fontSizes.XXL,
                }}
                $size={isMobile ? "S" : undefined}
                $weight={typography.fontWeights.SEMI_BOLD}
              >
                {t("api_slots.title")}
              </Title>
            )}
            <DescriptionWrapper>
              {API_SLOT_DESCRIPTION.map((item, index) => (
                <Flex
                  key={index}
                  $justifyContent="flex-start"
                  $alignItems="baseline"
                >
                  {isNewLook ? (
                    <NewLookCheck>
                      <Check size={14} strokeWidth={2} />
                    </NewLookCheck>
                  ) : (
                    <CheckCircleIcon />
                  )}
                  <Text>{t(item)}</Text>
                </Flex>
              ))}
            </DescriptionWrapper>
            <Divider />
          </>
        )}
        {numOfApiSlots}
        <Divider />
      </div>
    );
  }

  return (
    <Tooltip
      title={
        isLocked ? (
          <>
            <div>{t("api_slots.num_api_slots.locked_extend")}</div>
            <TypographyLink
              onClick={() => history.push(PURCHASE_API_SLOTS_SETTINGS_PATH)}
              style={{ display: "block", fontStyle: "italic" }}
            >
              {t("api_slots.num_api_slots.locked_extend_link")}
            </TypographyLink>
          </>
        ) : undefined
      }
    >
      <LockedWrapper $isLocked={isLocked}>
        <Wrapper $isBottomSummary={isBottomSummary} $isLocked={isLocked}>
          {numOfApiSlots}
        </Wrapper>
      </LockedWrapper>
    </Tooltip>
  );
};

export default ApiSlotsSelector;

const API_SLOT_DESCRIPTION: TranslationKeys<LanguageNameSpaces.BILLING>[] = [
  "api_slots.descriptions.connect_accounts",
  "api_slots.descriptions.multiple_exchanges",
  "api_slots.descriptions.diversify_trading_strategy",
  "api_slots.descriptions.increase_capacity",
];

const ApiSlotsTitleText = styled(Text).attrs({
  $size: typography.fontSizes.L,
  $weight: typography.fontWeights.SEMI_BOLD,
})`
  @media (max-height: ${SMALL_MOBILE_CHECKOUT_HEIGHT}) {
    font-size: ${(p) => p.theme.typography.fontSizes.M};
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A section title, like "Duration" above it. */
      && {
        font-size: 14px;
        line-height: 20px;
      }
    `}
`;
const Wrapper = styled.div<{ $isBottomSummary?: boolean; $isLocked?: boolean }>`
  margin: ${(p) => (p.$isBottomSummary ? "8px 0" : "0 0 16px 0")};
  ${(p) => p.$isLocked && "opacity: 0.5; pointer-events: none;"}
`;
const LockedWrapper = styled.div<{ $isLocked?: boolean }>`
  ${(p) => p.$isLocked && "cursor: not-allowed;"}
`;
const BorderVertical = styled.div`
  border-left: ${(p) => p.theme.borders.light};
  height: 20px;
`;
const IconWrapper = styled.div`
  width: 34px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  margin: 4px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: ${(p) =>
      p.theme.isDarkMode
        ? p.theme.colors.mediumBgColor
        : p.theme.colors.lightBackground};
  }
`;
const RemoveIcon = styled(BoldRemoveOutlined)`
  color: ${(p) => p.theme.colors.secondaryGrey};
`;
const PlusIcon = styled(BoldPlusOutlined)`
  color: ${(p) => p.theme.colors.secondaryGrey};
`;
const StyledInputNumber = styled(CustomInputNumber)`
  .ant-input-group .ant-input-group-addon {
    padding: 0;
  }
  &.ant-input-group-wrapper-outlined .ant-input-group-addon,
  .ant-input-outlined {
    border: none !important;
    box-shadow: unset;
    background: transparent;
  }
  .ant-input {
    height: 32px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      .ant-input-group {
        border: ${p.theme.borders.card};
        border-radius: ${p.theme.radii.control}px;
        background: ${p.theme.colors.cardBgColor};
        overflow: hidden;
      }
      .ant-input {
        text-align: center;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
        font-variant-numeric: tabular-nums;
      }
    `}
`;
const DescriptionWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 24px;
  row-gap: 20px;
  column-gap: 20px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 0;
      row-gap: 12px;
      > div {
        align-items: flex-start;
      }
      .ant-typography {
        font-size: 14px;
        line-height: 20px;
      }
      @media (max-width: 560px) {
        grid-template-columns: minmax(0, 1fr);
      }
    `}
`;
/** A plain check in the label grey (no tinted circle). */
const NewLookCheck = styled.span`
  flex: none;
  display: inline-flex;
  margin: 3px 8px 0 0;
  color: ${(p) => p.theme.colors.secondaryText};
`;
const CheckCircleIcon = styled(CheckCircleFilled)`
  color: ${(p) => p.theme.colors.primaryBlue};
  margin-right: 8px;
  top: 2px;
  position: relative;
  font-size: ${(p) => p.theme.typography.fontSizes.L};
`;
